/** Historical Imports words: the run register, the source formats and the closed-term records file. */
import type { ImportBatch, ImportKind, ReceiptAction } from "@/lib/api/imports";

export const importsWords = {
  // Run register
  findRun: "Find a run by its source name",
  allKinds: "All sources",
  closedTermRecords: "Closed-term records",
  registrarRoster: "Registrar roster",
  kind: "Source",
  mode: "Runs",
  appliedRuns: "Applied runs",
  dryRuns: "Dry runs",
  allRuns: "Applied runs and dry runs",
  noRunMatches: "No run matches this filter",
  refused: "Refused rows",
  failed: "Failed",
  running: "Running",
  // Run record
  source: "Source",
  started: "Started",
  finished: "Finished",
  notFinished: "Not finished",
  readNotices: "Read notices",
  readReceipts: "Read receipts",
  allOutcomes: "All outcomes",
  findReceipt: "Find a row by its key or reason",
  outcome: "Outcome",
  // Source formats
  fileFormat: "File format",
  downloadTemplate: "Download the template",
  templateNote: "Open it in a spreadsheet, replace the example rows with yours and save it as CSV. A JSON file of typed rows is read as well.",
  column: "Column",
  readsWhen: "Read on",
  noFileRead: "No file read",
  chooseFileOrTemplate: "Choose a file, or start from its template",
  // Closed-term records
  closedTermRecordsTask: "Bring in closed-term records",
  recordsJob: "Review a closed-term records file, choose every instructor and learner record, then apply it",
  recordsFile: "Records file",
  recordsIntro: "Past terms, their sections, instructors, learners, enrollments and certified results, one row each. Every row carries your own key, and rows refer to each other only by those keys.",
  recordsBounded: "Terms are recorded as closed and sections as archived. Nothing here grants portal access, sends mail or issues an invitation.",
  instructors: "Instructors",
  instructorsNeeded: "An instructor record is needed",
  instructorsChosen: "Every instructor record chosen",
  noInstructorRows: "This file names no instructor rows. A section's instructor keys must name rows in the file.",
  instructorsIntro: "The file names each instructor as source text. Choose the IMAM faculty record each one becomes; a name never chooses it.",
  instructorRecordFor: "Instructor record for",
  chooseRecord: "Choose a record",
  createAccountless: "Create an accountless historical record",
  archivedSuffix: " (Archived)",
  facultyCouldNotBeRead: "Faculty records could not be read.",
  tryAgain: "Try again",
  readingFaculty: "Reading faculty records",
  chooseEveryInstructor: "Choose every instructor's record.",
  readFirst: "Read a records file first.",
  fileCouldNotBeRead: "This file could not be read.",
} as const;

const KIND_LABEL: Record<ImportKind, string> = { historical: importsWords.closedTermRecords, registrar_roster: importsWords.registrarRoster };

export const importsText = {
  kindLabel: (kind: ImportKind | undefined) => KIND_LABEL[kind ?? "historical"],
  when: (value: string | null) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : importsWords.notFinished,
  /** One run's line: mode, source contract, when, and what it wrote or would write. */
  runFacts: (r: ImportBatch) => {
    const counts = r.mode === "dry_run"
      ? `${r.projected_create_count ?? 0} would be created · ${r.matched_count ?? 0} matched`
      : `${r.created_count ?? 0} created · ${r.matched_count ?? 0} matched`;
    const refused = r.rejected_count ? ` · ${r.rejected_count} refused` : "";
    const at = new Date(r.started_at).toLocaleTimeString(undefined, { timeStyle: "short" });
    return `${r.mode === "dry_run" ? "Dry run" : "Applied"} · ${KIND_LABEL[r.kind ?? "historical"]} · ${at} · ${counts}${refused}`;
  },
  refusedRows: (n: number) => `${n} refused ${n === 1 ? "row" : "rows"}`,
  receiptAction: (action: ReceiptAction) => ({ created: "Created", matched: "Matched", skipped: "Left out", rejected: "Refused",
    corrected: "Corrected", would_create: "Would be created" } as Record<ReceiptAction, string>)[action] ?? action,
  fileRows: (name: string, n: number) => `${name}: ${n} ${n === 1 ? "row" : "rows"}`,
  recordsSummary: (counts: Record<string, number>) => Object.entries(counts).filter(([, n]) => n > 0)
    .map(([k, n]) => { const noun = k === "faculty" ? "instructor" : k === "course" ? "section" : k; return `${n} ${noun}${n === 1 ? "" : "s"}`; }).join(" · "),
  rosterSummary: (c: { students: number; newStudents: number; enrollments: number }) =>
    `${c.students} ${c.students === 1 ? "student" : "students"} (${c.newStudents} new) · ${c.enrollments} ${c.enrollments === 1 ? "enrollment" : "enrollments"}`,
  instructorsUndecided: (n: number) => `Choose a record for ${n} ${n === 1 ? "instructor" : "instructors"}.`,
} as const;
